import $ from "jquery";
import _ from "underscore";
import Operation from "core/models/Operation";
import OperationSet from "core/models/OperationSet";
import ServiceSelector from "app/views/map/ServiceSelector";
import Layer from "app/models/Layer";
import ObjectLayerSelector from "app/views/map/ObjectLayerSelector";
import GeoUtils from "app/utils/GeoUtils";
import Field from "core/models/Field";
import FormInputView from "core/views/FormInputView";
import auth from "app/auth";
import async from "async";
import ModelDirectoryView from "app/views/map/ModelDirectoryView";
import ErrorUtils from "app/utils/ErrorUtils";
import session from "app/Session";
import clipboard from "core/Clipboard";
import urlService from "app/UrlService";
import isMobile from "core/is-mobile";
import mapContext from "app/views/map/MapContext";

export default new OperationSet([
  new Operation({
    id: "remove-service",
    text: "Remove Service",
    icon: "minus",
    action: function (model) {
      mapContext.getConfiguration().removeService(model);
      mapContext.setSelectedService(null);

      return Operation.resolve(model, "Service removed from map.");
    },
  }),

  new Operation({
    id: "delete-service",
    text: "Delete Service",
    icon: "trash",
    action: function (model) {
      var deferred = new $.Deferred();

      ServiceSelector.destroyService(model.service, function (result) {
        if (result === true) {
          //destruction was cancelled
          deferred.reject(false);
        } else if (result) {
          //an error occurred
          deferred.reject(result);
        } else {
          //it worked!
          mapContext.getConfiguration().removeService(model);
          mapContext.setSelectedService(null);
          deferred.resolve(model, "Service deleted successfully");
        }
      });

      return deferred.promise();
    },
    available: function (model) {
      return (
        model &&
        session.can(["delete", "own"], {
          MapService: model.service,
        })
      );
    },
  }),

  new Operation({
    id: "move-service",
    text: "Move Service",
    icon: "move",
    action: function (model, options) {
      mapContext
        .getConfiguration()
        .moveService(options.oldIndex - 1, options.newIndex - 1);

      return Operation.resolve(model, "Service moved successfully.");
    },
  }),

  new Operation({
    id: "add-layer",
    text: "Add Layer",
    icon: "leaf",
    action: function (model) {
      var deferred = new $.Deferred();

      //present a dialog from which the user can create a new "layer" (Layer), which will get added to the selected
      //service and TODO the selected layer, if any.
      ObjectLayerSelector.selectLayer(
        model,
        {
          confirmText: {
            type: "primary",
            text: "Create",
          },
          title: "Add Layer",
          createOnly: true,
          canCreate: Layer.allowCreate(session, model ? model.service : null),
          noUpload: isMobile,
        },
        function (err, layers) {
          if (err) {
            if (_.isBoolean(err)) {
              return deferred.reject(false);
            } else {
              return deferred.reject(err);
            }
          }

          //After addLayer submission, we could be adding one or multiple layers to the map.
          if (!_.isArray(layers) && layers) {
            layers = [layers];
          }

          _.each(layers, function (layer) {
            model.addLayer(layer);
          });
          deferred.resolve(model, "Layer added successfully.");
        }
      );

      return deferred.promise();
    },
    available: function (model) {
      return (
        model &&
        !model.service.get("external") &&
        Layer.allowCreate(session, model.service)
      );
    },
  }),

  new Operation({
    id: "paste-layer",
    text: "Paste",
    icon: "paste",
    action: function (model) {
      var data = clipboard.get("layer-structure");
      var deferred = new $.Deferred();
      var layer = new Layer(data.layerData);

      layer.set(data.layerData);
      layer.collection = model.featureLayers;
      layer.getFields().add(data.fieldData);
      layer
        .save()
        .then(function () {
          return layer.getFields().save();
        })
        .done(function () {
          ObjectLayerSelector.createLayer(model, layer, function (err, layer) {
            if (err) {
              return deferred.reject(err);
            }

            model.addLayer(layer);
            deferred.resolve(model, "Layer copied successfully.");
          });
        })
        .fail(function (err) {
          deferred.reject(err);
        });

      return deferred.promise();
    },
    available: function (model) {
      return (
        model &&
        Layer.allowCreate(session, model.service) &&
        clipboard.has("layer-structure")
      );
    },
  }),

  new Operation({
    id: "edit-service-security",
    text: "Manage Service Security",
    icon: "user",
    action: function (model) {
      return mapContext.getOperation("manage-security").action(model, {
        type: {
          MapService: model.service,
        },
        actions: {
          read: "View the service.",
          update: "Update properties of the service, including security.",
          delete: "Delete the service.",
          "form:read": "View objects and properties of any layer.",
          "form:delete": "Delete any layer.",
          "form:update": "Update properties of any layer, including security.",
          "form:create": "Add a layer to the service.",
          "form:add": "Add objects to any layer.",
          "form:remove": "Remove objects from any layer.",
          "form:modify": "Modify objects on any layer.",
        },
      });
    },
    available: function (model) {
      return (
        model &&
        session.can(["update", "own"], {
          MapService: model.service,
        })
      );
    },
  }),

  new Operation({
    id: "save-service",
    text: "Save Service",
    icon: "floppy-disk",
    action: function (model) {
      var deferred = new $.Deferred();

      model.service.save(null, {
        success: function () {
          deferred.resolve(model, "Service saved successfully.");
        },
        error: function (model, response) {
          deferred.reject(response);
        },
      });

      return deferred.promise();
    },
    available: function (model) {
      return (
        model &&
        session.can(["update", "own"], {
          MapService: model.service,
        })
      );
    },
  }),

  new Operation({
    id: "select-service",
    text: "Select",
    action: function (model) {
      mapContext.getConfiguration().setActiveService(model);

      return Operation.resolve(model);
    },
  }),

  new Operation({
    id: "service-select-all",
    text: "Select All Records",
    action: function (model) {
      var models = [];
      var deferred = new $.Deferred();

      model.eachLayer(function (layer) {
        var currentModels = layer.getGeometries();

        _.each(currentModels, function (model) {
          model.originalLayer = layer;
        });

        models = models.concat(currentModels);
      });

      //todo extract each operation to its own file so we can import them separately
      mapContext.clearSelection();
      mapContext.setSelection(models);
      mapContext
        .getOperation("highlight-geometry")
        .action(models)
        .then(function () {
          return mapContext
            .getOperation("show-geometry-details")
            .action(models);
        })
        .then(
          function () {
            deferred.resolve(model);
          },
          function (err) {
            deferred.reject(err);
          }
        );

      return deferred.promise();
    },
    available: function (model) {
      return model && !model.service.get("external");
    },
  }),

  new Operation({
    id: "service-tabulate-all",
    text: "View All Records",
    action: function (model) {
      var models = [];

      model.eachLayer(function (layer) {
        var currentModels = layer.getGeometries();

        _.each(currentModels, function (model) {
          model.originalLayer = layer;
        });

        models = models.concat(currentModels);
      });

      return mapContext
        .getOperation("export-geometry-table")
        .action(models)
        .then(function () {
          return Operation.resolve(model);
        });
    },
    available: function (model) {
      return model && !model.service.get("external");
    },
  }),

  /**
   * Adds new layer(s) to a service, which can be pre-populated with features contained in an uploaded file.
   * @param {object} model The uploadView which holds the file & new layer(s).
   * @param {object} [options.serviceLayer] A serviceLayer to which the layer(s) will be attached.
   * @param {Function} [options.create] Function which shall be called if a layer is created *successfully*. The first argument is the `ObjectLayer` instance that was created.
   * @param  {promise} A deferred promise that resolves upon successfully adding a layer.
   */
  new Operation({
    id: "add-layer-upload",
    icon: "",
    text: "Add Layer Upload",
    action: function (model, options) {
      var deferred = new $.Deferred();

      var serviceLayer = options.serviceLayer;

      model
        .save()
        .done(function (data) {
          var allLayers = [];

          async.each(
            data,
            function (formData, cb) {
              var layer = new Layer(
                _.extend(
                  {
                    hasGeometry: true,
                  },
                  formData
                )
              );

              layer.collection = serviceLayer.featureLayers;
              layer.fetch({
                success: function (form) {
                  ObjectLayerSelector.createLayer(
                    serviceLayer,
                    layer,
                    function (err, layer) {
                      layer
                        .load()
                        .done(function () {
                          options.create(serviceLayer, layer);
                          allLayers.push(layer);
                          cb(null, layer);
                        })
                        .fail(function (err) {
                          cb(err, "Failed loading the layer");
                        });
                    }
                  );
                },
                error: function (model, response) {
                  cb(response, "Failed fetching the Layer");
                },
              });
            },
            function (err, response) {
              if (err) {
                return deferred.reject(err, response);
              }

              deferred.resolve(allLayers);
            }
          );
        })
        .fail(function (err) {
          deferred.reject(err, "Failed saving the layer");
        });

      return deferred.promise();
    },
  }),

  /**
   * Adds one new layer to the service with no pre populated features.
   * @param {object} model The EditorView which holds the new layer.
   * @param {object} [model.model] The new layer to be created
   * @param {object} [options.serviceLayer] A serviceLayer to which the layer will be attached.
   * @param {Function} [options.create] Function which shall be called if a layer is created *successfully*. The first argument is the `ObjectLayer` instance that was created.
   * @param  {promise} A deferred promise that resolves upon successfully adding a layer.
   */
  new Operation({
    id: "add-layer-blank",
    icon: "",
    text: "Add Blank Layer",
    action: function (model, options) {
      var deferred = new $.Deferred();

      var layerModel = model.model;
      var serviceLayer = options.serviceLayer;

      model
        .save()
        .done(function () {
          ObjectLayerSelector.createLayer(serviceLayer, layerModel, function (
            err,
            layer
          ) {
            layer
              .load()
              .done(function () {
                options.create(serviceLayer, layer);
                return deferred.resolve(layer);
              })
              .fail(function (err) {
                return deferred.reject(null, err);
              });
          });
        })
        .fail(function (model, response) {
          return deferred.reject(model, response);
        });

      return deferred.promise();
    },
  }),
]);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/operations/map/MapServiceBaseOperations.js
// module id = 884
// module chunks = 1